import { useState } from "react";
import { toast } from "sonner";
import { Lightbulb, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

/** Popup form for submitting a feature request/idea. Saves to the site for admin review. */
export default function FeatureRequestModal({ open, onClose }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim() || saving) return;
        setSaving(true);
        try {
            await api.post("/feature-requests", { title: title.trim(), description: description.trim() });
            toast.success("Thanks! Your idea was sent to the team.");
            setTitle(""); setDescription("");
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send — please try again");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto p-4" data-testid="feature-request-modal"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <div className="relative my-16 w-full max-w-md border border-[#2a2a2a] bg-[#141414] p-6 shadow-2xl">
                <button type="button" onClick={onClose} data-testid="feature-request-close"
                    className="absolute top-3 right-3 text-[#6a6a6a] hover:text-white"><X className="h-4 w-4" /></button>
                <div className="flex items-center gap-2 mb-1">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ff4500]/10 text-[#ff4500]"><Lightbulb className="h-5 w-5" /></span>
                    <h3 className="font-display text-lg font-bold text-white">Request a Feature</h3>
                </div>
                <p className="text-xs text-[#a0a0a0] mb-4">Have an idea to make TravelSports.info better? Tell us — we read every one.</p>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#7a7a7a]">Title</label>
                        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140}
                            data-testid="feature-request-title" placeholder="e.g. Add a team calendar export"
                            className="mt-1 w-full bg-[#0f0f0f] border border-[#2a2a2a] px-3 py-2 text-sm text-white outline-none focus:border-[#ff4500]" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#7a7a7a]">Details (optional)</label>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={5000}
                            data-testid="feature-request-description" placeholder="What should it do, and who is it for?"
                            className="mt-1 w-full resize-none bg-[#0f0f0f] border border-[#2a2a2a] px-3 py-2 text-sm text-white outline-none focus:border-[#ff4500]" />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-1">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#a0a0a0] hover:text-white">Cancel</button>
                        <button type="submit" disabled={!title.trim() || saving} data-testid="feature-request-submit"
                            className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-white bg-[#ff4500] hover:bg-[#ff6a33] disabled:opacity-40 inline-flex items-center gap-1.5">
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Send
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
